import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-forest hover:underline mb-8">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>
        
        <h1 className="font-heading font-bold text-3xl mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: February 2025</p>
        
        <div className="prose prose-stone max-w-none space-y-6">
          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground">
              By accessing or using Rise for Bakeries CRM ("Rise," "the Service"), you agree to be bound by these 
              Terms of Service. If you do not agree to these terms, please do not use our Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">2. Description of Service</h2>
            <p className="text-muted-foreground">
              Rise is a lead generation and customer relationship management platform designed for wholesale bakeries. 
              The Service allows you to discover potential business leads, manage customer relationships, 
              draft personalized outreach emails, and schedule meetings.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">3. Account Registration</h2>
            <p className="text-muted-foreground mb-3">To use Rise, you must:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Sign in using a valid Google account</li>
              <li>Be authorized by an administrator to access the application</li>
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">4. Acceptable Use</h2>
            <p className="text-muted-foreground mb-3">You agree to use Rise only for lawful purposes. You must not:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Send spam or unsolicited bulk emails</li>
              <li>Violate any applicable anti-spam laws (CAN-SPAM, GDPR, etc.)</li>
              <li>Use the Service to harass, abuse, or harm others</li>
              <li>Attempt to gain unauthorized access to the Service</li>
              <li>Use the Service for any illegal or fraudulent activity</li>
              <li>Scrape or collect data in violation of third-party terms of service</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">5. Email Compliance</h2>
            <p className="text-muted-foreground mb-3">
              When using Rise to send emails, you are responsible for ensuring compliance with all applicable 
              email marketing laws, including:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Including accurate sender information</li>
              <li>Honoring opt-out requests promptly</li>
              <li>Not using deceptive subject lines</li>
              <li>Including a physical mailing address</li>
            </ul>
            <p className="text-muted-foreground mt-3">
              Rise requires you to approve every email before it is sent. You are solely responsible for 
              the content of emails sent through our Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">6. Google Services Integration</h2>
            <p className="text-muted-foreground">
              Rise integrates with Google services (Gmail, Google Calendar) to provide email and scheduling 
              functionality. Your use of these integrations is also subject to Google's Terms of Service. 
              You can disconnect Google services at any time through the Settings page.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">7. Intellectual Property</h2>
            <p className="text-muted-foreground">
              The Service and its original content, features, and functionality are owned by Rise and are 
              protected by international copyright, trademark, and other intellectual property laws. 
              You retain ownership of any content you create or upload to the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">8. Third-Party Services</h2>
            <p className="text-muted-foreground">
              Rise uses third-party services for lead discovery (including search APIs and public business 
              directories). We do not guarantee the accuracy or completeness of data obtained from these sources. 
              You are responsible for verifying information before taking action on it.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">9. AI-Generated Content</h2>
            <p className="text-muted-foreground">
              Rise uses artificial intelligence to analyze leads and draft email content. AI-generated 
              content is provided as suggestions only. You are responsible for reviewing, editing, and 
              approving all content before it is sent. We do not guarantee the accuracy, appropriateness, 
              or effectiveness of AI-generated content.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">10. Limitation of Liability</h2>
            <p className="text-muted-foreground">
              To the maximum extent permitted by law, Rise shall not be liable for any indirect, incidental, 
              special, consequential, or punitive damages, including loss of profits, data, or business 
              opportunities, arising from your use of the Service. Our total liability shall not exceed 
              the amount you paid for the Service in the twelve months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">11. Disclaimer of Warranties</h2>
            <p className="text-muted-foreground">
              The Service is provided "as is" and "as available" without warranties of any kind, either 
              express or implied. We do not warrant that the Service will be uninterrupted, secure, or 
              error-free, or that any defects will be corrected.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">12. Termination</h2>
            <p className="text-muted-foreground">
              We may terminate or suspend your access to the Service immediately, without prior notice, 
              for any reason, including breach of these Terms. Upon termination, your right to use the 
              Service will cease immediately. You may request export of your data before termination.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">13. Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will provide notice of significant 
              changes by posting the new Terms on this page. Your continued use of the Service after changes 
              constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">14. Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-semibold text-xl mb-3">15. Contact Us</h2>
            <p className="text-muted-foreground">
              If you have any questions about these Terms of Service, please contact us at:{' '}
              <a href="mailto:hello@maddonutshop.com" className="text-forest hover:underline">
                hello@maddonutshop.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
